import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenT90Report from "./HiddenT90Report";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Transition 90 readiness bands, exactly mirroring t90-scoring.js's
//    getInterpretationBand() (80 / 65 / 50 thresholds) ─────────────────────
const getReadinessBand = (pct) => {
  if (pct >= 80) return { level: "strong",   label: "Core Strength" };
  if (pct >= 65) return { level: "estab",    label: "Established Strength" };
  if (pct >= 50) return { level: "emerging", label: "Emerging Strength" };
  return             { level: "dev",       label: "Development Priority" };
};

// ── Readiness pill tokens (same visual language as T90Report.jsx's chipColor) ──
const READINESS_TOKENS = {
  strong:   { bg: "#eaf3e6", color: "#2a5010", border: "#cde4c6" },
  estab:    { bg: "#e6f1ec", color: "#0f2744", border: "#c7ddd3" },
  emerging: { bg: "#fdf3e0", color: "#8a5a00", border: "#f5dfa8" },
  dev:      { bg: "#fbeaea", color: "#a81026", border: "#f3c7c9" },
};

// ── Score-badge tokens for the three KPIs on each row ─────────────────────
const SCORE_TOKENS = {
  overall: { bg: "#fef2f2", color: "#a81026", border: "#f6c9cd" },
  mindset: { bg: "#eef2ff", color: "#3b5ec0", border: "#c7d2fe" },
  skill:   { bg: "#fff8ec", color: "#8a5a00", border: "#f0dba8" },
};

const hexToRgb = (h) => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
const rgbToHex = ([r, g, b]) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
const avgHex = (hexes) => {
  const channels = hexes.map(hexToRgb);
  return rgbToHex([0, 1, 2].map((i) => channels.reduce((s, c) => s + c[i], 0) / channels.length));
};

// ── ReadinessPill — same single-token pill pattern as VAK's DominantStylePill
//    (T90 readiness is always a single band, so no multi-blend case needed) ──
const ReadinessPill = ({ pct }) => {
  if (pct === undefined || pct === null) return <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>;

  const band = getReadinessBand(pct);
  const c = READINESS_TOKENS[band.level];

  const pillBase = {
    padding:      "4px 12px",
    borderRadius: "999px",
    fontSize:     "12px",
    fontWeight:   "800",
    whiteSpace:   "nowrap",
    display:      "inline-block",
    width:        "max-content",
    lineHeight:   "1.6",
  };

  return (
    <span style={{ ...pillBase, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {band.label} ({pct}%)
    </span>
  );
};

export default function GlobalT90Responses({ surveyId }) {
  const [t90Data,        setT90Data]        = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Filters ──
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterReadiness, setFilterReadiness] = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterDept,      setFilterDept]      = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchT90Results();
  }, [surveyId]);

  const fetchT90Results = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/t90/all`,
        { params: { survey_id: surveyId } }
      );
      setT90Data(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch Transition 90 results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  // Collect unique dropdown options
  const uniqueReadiness = [
    ...new Set(t90Data.map(u => getReadinessBand(u.overall?.pct || 0).label)),
  ].sort();

  const uniqueCompanies = [
    ...new Set(t90Data.map(u => (u.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(t90Data.map(u => (u.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = t90Data.filter((user) => {
    const name    = (user.name    || "").toLowerCase();
    const email   = (user.email   || "").toLowerCase();
    const company = (user.company    || "").trim().toLowerCase();
    const dept    = (user.department || "").trim().toLowerCase();
    const readinessLabel = getReadinessBand(user.overall?.pct || 0).label;

    const matchName       = name.includes(filterName.toLowerCase());
    const matchEmail      = email.includes(filterEmail.toLowerCase());
    const matchCompany    = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept       = filterDept    === "" || dept    === filterDept.toLowerCase();
    const matchReadiness  =
      filterReadiness === "" ||
      readinessLabel.toLowerCase() === filterReadiness.toLowerCase();

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchReadiness && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterReadiness("");
    setFilterCompany("");
    setFilterDept("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts — by readiness band label ──
  const readinessCounts = t90Data.reduce((acc, u) => {
    const key = getReadinessBand(u.overall?.pct || 0).label;
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const formattedData = {
      respondent: {
        name:            user.name       || "Participant",
        email:           user.email      || "",
        phone:           user.phone      || "",
        company:         user.company    || "",
        department:      user.department || "",
        submission_time: user.submission_time,
      },
      overall: user.overall || { pct: 0 },
      mindset: user.mindset || { pct: 0, dimensions: [] },
      skill:   user.skill   || { pct: 0, dimensions: [] },
      radar:   user.radar   || { mindset: { codes: [], values: [] }, skill: { codes: [], values: [] } },
      submission_time: user.submission_time,
    };

    setDownloadData(formattedData);
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading Transition 90 data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>

      <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer — keyed so a fresh instance (fresh DOM, fresh
          font <link>) mounts for every download, instead of reusing one
          permanently-mounted instance across clicks. This mirrors how
          T90Report.jsx / HiddenT90Report.jsx already work correctly. */}
      <HiddenT90Report
        key={isGeneratingRow}
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "15px",
      }}>
        <h3 style={{ margin: 0 }}>📋 Transition 90 Results ({filteredData.length})</h3>
        <span style={{
          background: "#0f2744", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Readiness summary pills ── */}
      {t90Data.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {Object.entries(readinessCounts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([label, count]) => {
              const level = Object.keys(READINESS_TOKENS).find(
                (lvl) => READINESS_TOKENS[lvl] && getReadinessBandLabelFor(lvl) === label
              );
              const c = READINESS_TOKENS[level] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
              const isActive = filterReadiness.toLowerCase() === label.toLowerCase();

              return (
                <span
                  key={label}
                  onClick={() => setFilterReadiness(isActive ? "" : label)}
                  title={`Click to filter by ${label}`}
                  style={{
                    background:    c.bg,
                    color:         c.color,
                    border:        `1px solid ${c.border}`,
                    padding:       "4px 12px",
                    borderRadius:  "999px",
                    fontSize:      "12px",
                    fontWeight:    "700",
                    cursor:        "pointer",
                    display:       "inline-block",
                    width:         "max-content",
                    whiteSpace:    "nowrap",
                    outline:       isActive ? `2px solid ${c.color}` : "none",
                    outlineOffset: "2px",
                    transition:    "outline 0.15s",
                  }}
                >
                  {label}: {count}
                </span>
              );
            })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Readiness</label>
          <select
            value={filterReadiness}
            onChange={(e) => setFilterReadiness(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Readiness Levels</option>
            {uniqueReadiness.map(r => (
              <option key={r} value={r.toLowerCase()}>{r}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {t90Data.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No Transition 90 responses found.
        </div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No responses match your current filters.
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "220px" }}>Scores</th>
                <th style={{ padding: "12px", minWidth: "200px", background: "#fdf6e3" }}>
                  📊 T90 Readiness
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const overallPct = user.overall?.pct || 0;
                const mindsetPct = user.mindset?.pct || 0;
                const skillPct   = user.skill?.pct   || 0;
                const isGenerating = isGeneratingRow === index;

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user.email}<br />
                        {user.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Scores ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {[
                          { label: "Overall", val: overallPct, c: SCORE_TOKENS.overall },
                          { label: "Mindset", val: mindsetPct, c: SCORE_TOKENS.mindset },
                          { label: "Skill",   val: skillPct,   c: SCORE_TOKENS.skill   },
                        ].map(({ label, val, c }) => (
                          <span
                            key={label}
                            style={{
                              background:   c.bg,
                              color:        c.color,
                              border:       `1px solid ${c.border}`,
                              padding:      "2px 8px",
                              borderRadius: "4px",
                              fontSize:     "12px",
                              fontWeight:   "700",
                              whiteSpace:   "nowrap",
                            }}
                          >
                            {label}: {val}%
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* ── T90 Readiness — pill matching T90Report.jsx's chip colours ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      <ReadinessPill pct={overallPct} />
                    </td>

                    {/* ── Actions ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Small helper used by the summary-pill block to map a readiness *level*
// key (e.g. "strong") back to its display label (e.g. "Strongly developed"),
// since READINESS_TOKENS is keyed by level but readinessCounts is keyed by label.
function getReadinessBandLabelFor(level) {
  return {
    strong:   "Strongly developed",
    estab:    "Generally established",
    emerging: "Emerging / inconsistent",
    dev:      "Development priority",
  }[level];
}